import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import billingService, { formatMxn } from '../../utils/billingService';
import authService from '../../utils/authService';

/**
 * Banner shown above employee/payroll-receipt lists when the company is overage.
 *
 * Props:
 *   - enforcement: optional plan_enforcement object from a response meta.
 *     Shape: { is_overage, total_active, visible_limit, hidden, recommended_plan, current_plan }
 *     If not provided, the banner self-fetches from /api/billing/employee-usage.
 *   - autoFetch: when true (default), fetches enforcement info if not provided.
 *
 * Only renders for users with role=company. Hides itself otherwise.
 */
const EnforcementBanner = ({ enforcement, autoFetch = true }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(enforcement || null);
  const [loaded, setLoaded] = useState(!!enforcement);

  const user = authService?.getCurrentUser();
  const isCompany = user?.role === 'company';

  useEffect(() => {
    if (enforcement) {
      setInfo(enforcement);
      setLoaded(true);
      return;
    }
    if (!autoFetch || !isCompany) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await billingService.getEmployeeUsage();
        if (cancelled) return;
        const d = res?.data;
        if (!d) return;
        setInfo({
          is_overage: d.isOverage,
          total_active: d.activeEmployees,
          visible_limit: d.visibleLimit >= Number.MAX_SAFE_INTEGER ? null : d.visibleLimit,
          hidden: d.hiddenEmployees,
          recommended_plan: d.recommendedPlan
            ? {
                id: d.recommendedPlan.id,
                name: d.recommendedPlan.name,
                price_mxn: Number(d.recommendedPlan.price_mxn),
                max_employees: d.recommendedPlan.max_employees,
              }
            : null,
          current_plan: d.currentPlan
            ? {
                id: d.currentPlan.id,
                name: d.currentPlan.name,
                max_employees: d.currentPlan.max_employees,
              }
            : null,
        });
        setLoaded(true);
      } catch {
        // silent — banner just won't show
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enforcement, autoFetch, isCompany]);

  if (!isCompany) return null;
  if (!loaded || !info?.is_overage) return null;

  const hidden = info.hidden ?? 0;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-3 flex-1">
        <Icon name="AlertTriangle" size={22} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {hidden} {hidden === 1 ? 'empleado' : 'empleados'}{' '}
            {hidden === 1 ? 'no es visible' : 'no son visibles'} bajo tu plan actual.
          </p>
          <p className="text-sm text-amber-800 mt-0.5">
            Plan actual: <strong>{info.current_plan?.name || 'sin plan'}</strong>
            {info.current_plan?.max_employees != null
              ? ` (límite ${info.current_plan.max_employees} empleados)`
              : ''}
            . Total de empleados activos: {info.total_active}.
            {info.recommended_plan && (
              <>
                {' '}
                Recomendamos cambiar a <strong>{info.recommended_plan.name}</strong>{' '}
                ({formatMxn(info.recommended_plan.price_mxn)}/mes).
              </>
            )}
          </p>
        </div>
      </div>
      <Button variant="primary" onClick={() => navigate('/app/account/plans/upgrade')}>
        Cambiar plan
      </Button>
    </div>
  );
};

export default EnforcementBanner;
